import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, User, Lock, Mail, KeyRound, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isNativeApp = !!window.Capacitor?.isNativePlatform?.();

// Night sky background component shared by Login/Register on APK
const NightSkyBg = () => (
  <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #0a0a1a 0%, #0d1b2a 40%, #1b2838 100%)' }}>
    {[...Array(30)].map((_, i) => (
      <div key={i} className="absolute rounded-full" style={{
        width: `${i % 5 === 0 ? 2 : 1}px`, height: `${i % 5 === 0 ? 2 : 1}px`,
        left: `${(i * 23 + 7) % 96}%`, top: `${(i * 17 + 3) % 94}%`,
        opacity: 0.2 + (i % 4) * 0.15,
        background: i % 5 === 0 ? 'rgba(212,175,55,0.9)' : 'white',
        animation: `loginTwinkle ${2 + (i % 3) * 1.5}s ease-in-out infinite ${i * 0.3}s`
      }} />
    ))}
    <style>{`@keyframes loginTwinkle { 0%, 100% { opacity: 0.1; } 50% { opacity: 0.8; } }`}</style>
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  
  // Forgot password flow
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState('email'); // 'email', 'otp', 'done'
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(formData.username, formData.password);
    setLoading(false);
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/chats');
    } else {
      toast.error(result.error);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error('Please enter your email'); return; }
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success(res.data.message);
      setForgotStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    }
    setForgotLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, { email: forgotEmail, otp, new_password: newPassword });
      toast.success('Password reset successfully! You can now login.');
      setShowForgotPassword(false);
      setForgotStep('email');
      setForgotEmail(''); setOtp(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
    setForgotLoading(false);
  };

  return (
    <div className={`min-h-screen flex flex-col relative ${isNativeApp ? '' : 'bg-background-DEFAULT'}`} data-testid="login-page">
      {isNativeApp && <NightSkyBg />}
      <div className="relative z-10 p-6">
        <button data-testid="back-btn" onClick={() => navigate('/')} className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${isNativeApp ? 'hover:bg-white/10' : 'hover:bg-secondary-DEFAULT'}`}>
          <ArrowLeft size={20} className={isNativeApp ? 'text-white' : 'text-text-primary'} />
        </button>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className={`rounded-3xl p-8 ${isNativeApp ? 'bg-white/10 backdrop-blur-lg border border-white/10' : 'bg-white shadow-card border border-border-DEFAULT'}`}>
            
            {!showForgotPassword ? (
              <>
                <div className="text-center mb-8">
                  <h2 className={`font-heading text-3xl mb-2 ${isNativeApp ? 'text-white' : 'text-text-primary'}`}>Welcome Back</h2>
                  <p className={`text-sm ${isNativeApp ? 'text-gray-400' : 'text-text-secondary'}`}>Sign in to continue</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>Username</label>
                    <div className="relative">
                      <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input data-testid="username-input" type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className={`w-full h-12 border-none rounded-xl pl-12 pr-4 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="Your username" required />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>Password</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input data-testid="password-input" type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className={`w-full h-12 border-none rounded-xl pl-12 pr-12 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="Your password" required />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-[#D4AF37] hover:underline" data-testid="forgot-password-link">
                      Forgot Password?
                    </button>
                  </div>

                  <button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full h-12 px-8 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium shadow-gold hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                    {loading ? 'Signing In...' : 'Sign In'}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <p className={`text-sm ${isNativeApp ? 'text-gray-400' : 'text-text-secondary'}`}>
                    Don't have an account?{' '}
                    <button onClick={() => navigate('/register')} className="text-[#D4AF37] font-medium hover:underline">Create one</button>
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                    <KeyRound size={28} className="text-[#D4AF37]" />
                  </div>
                  <h2 className={`font-heading text-2xl mb-2 ${isNativeApp ? 'text-white' : 'text-text-primary'}`}>Reset Password</h2>
                  <p className={`text-sm ${isNativeApp ? 'text-gray-400' : 'text-text-secondary'}`}>
                    {forgotStep === 'email' ? 'Enter your registered email to receive an OTP' : 'Enter the OTP and your new password'}
                  </p>
                </div>

                {forgotStep === 'email' ? (
                  <form onSubmit={handleRequestOTP} className="space-y-5">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>Email Address</label>
                      <div className="relative">
                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input data-testid="forgot-email-input" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className={`w-full h-12 border-none rounded-xl pl-12 pr-4 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="your@email.com" required />
                      </div>
                    </div>
                    <button type="submit" disabled={forgotLoading} className="w-full h-12 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium disabled:opacity-50" data-testid="send-otp-btn">
                      {forgotLoading ? 'Sending...' : 'Send OTP'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>OTP Code</label>
                      <input data-testid="otp-input" type="text" value={otp} onChange={(e) => setOtp(e.target.value)} className={`w-full h-12 border-none rounded-xl px-4 text-center text-2xl font-mono tracking-[0.5em] focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="000000" maxLength={6} required />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>New Password</label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input data-testid="new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={`w-full h-12 border-none rounded-xl pl-12 pr-4 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="At least 6 characters" required />
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>Confirm New Password</label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input data-testid="confirm-new-password-input" type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} className={`w-full h-12 border-none rounded-xl pl-12 pr-4 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="Re-enter new password" required />
                      </div>
                    </div>
                    <button type="submit" disabled={forgotLoading} className="w-full h-12 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium disabled:opacity-50" data-testid="reset-password-btn">
                      {forgotLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </form>
                )}

                <div className="mt-6 text-center">
                  <button onClick={() => { setShowForgotPassword(false); setForgotStep('email'); }} className={`text-sm hover:underline ${isNativeApp ? 'text-gray-400' : 'text-text-secondary'}`} data-testid="back-to-login-link">
                    Back to Login
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
